/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import { usePrevious } from '@gitbook/util-react-hooks';
import { isHttpUrl } from '@gitbook/util-url';

import { Button, type ButtonProps, type GenericButtonHookProps, useButton } from '../Button';
import { type ColorTheme, ThemeContext } from '../Theme';

export interface ImageResizeOptions {
    dpr?: number;
    width?: number;
    height?: number;
    fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
}

type ImageResizeFn = (url: string, options: ImageResizeOptions) => string;

/**
 * By default, we use a width of 256px for resizing images.
 * (When no `width` or `height` is provided to the component)
 */
const RESIZE_DEFAULT_WIDTH = 256;

interface ImageContextValue {
    getResizedURL: ImageResizeFn;
}

/**
 * Context for processing images.
 */
const ImageContext = React.createContext<ImageContextValue>({
    getResizedURL: (url) => url,
});

/**
 * Provider for how images will be processed.
 */
export function ImageProvider(props: React.PropsWithChildren<ImageContextValue>) {
    const { getResizedURL, children } = props;
    const value = React.useMemo(() => ({ getResizedURL }), [getResizedURL]);
    return <ImageContext.Provider value={value}>{children}</ImageContext.Provider>;
}

export type ImageSource = string | { light: string; dark: string };

export interface ImageProps extends Omit<React.ComponentPropsWithRef<'img'>, 'src'> {
    /**
     * Whether the image should be rounded or not.
     */
    rounded?: boolean;

    /**
     * Whether the image should be resized or not.
     * By default, the image will be resized:
     * - Using the `resize` prop if provided.
     * - Using the `width` and `height` props if provided.
     * - Using a default width of 256px if no width is provided.
     *
     * Passing `false` will disable resizing.
     */
    resize?: false | ImageResizeOptions;

    /**
     * Source of the image.
     * Can be a string or an object with `light` and `dark` URLs.
     */
    src?: ImageSource | undefined;
}

/**
 * Resolved source of the image.
 */
function useResolvedSrc(props: ImageProps, theme: ColorTheme) {
    const ctx = React.useContext(ImageContext);
    const resizeOptions = React.useMemo(
        () =>
            getImageResize({
                width: props.width,
                height: props.height,
                resize: props.resize,
            }),
        [props.resize, props.width, props.height]
    );
    const getResizedURL = React.useCallback(
        (url: string) => {
            if (!isHttpUrl(url) || !resizeOptions) {
                return url;
            }
            return ctx.getResizedURL(url, resizeOptions);
        },
        [ctx.getResizedURL, resizeOptions]
    );
    const themeSrc = React.useMemo(() => {
        if (!props.src || typeof props.src === 'string') {
            return props.src;
        }
        return props.src[theme];
    }, [props.src, theme]);
    return React.useMemo(() => {
        return themeSrc ? getResizedURL(themeSrc) : themeSrc;
    }, [themeSrc, getResizedURL]);
}

/**
 * A simple Image component that wraps the <img> element.
 *
 * Not doing much at the moment other than defaulting to an empty alt tag and resizing.
 * We might want to change some defaults for loading, handle responsiveness, etc.
 */
export function Image({ ref, ...props }: ImageProps) {
    const {
        src: propSrc,
        width = 'auto',
        height = 'auto',
        rounded = true,
        resize,
        className,
        ...rest
    } = props;
    const src = useResolvedSrc(props, 'light');
    const imgElement = (
        <img
            ref={ref}
            src={src}
            className={clsx('overflow-hidden', rounded && 'rounded-lg', className)}
            width={width}
            height={height}
            {...rest}
        />
    );
    if (props.src && typeof props.src !== 'string') {
        return <ThemedImage {...props}>{imgElement}</ThemedImage>;
    }
    return imgElement;
}

function ThemedImage(props: ImageProps & { children?: React.ReactNode }) {
    const theme = React.useContext(ThemeContext);
    const darkSrc = useResolvedSrc(props, 'dark');
    return (
        <picture className="contents">
            <source srcSet={darkSrc} media={theme === 'dark' ? 'all' : 'none'} />
            {props.children}
        </picture>
    );
}

/**
 * Compute the resize options for an image.
 */
function getImageResize(
    options: Pick<ImageProps, 'resize' | 'width' | 'height'>
): ImageResizeOptions | null {
    if (options.resize === false) {
        return null;
    }

    const height = typeof options.height === 'number' ? options.height : undefined;
    const width =
        typeof options.width === 'number'
            ? options.width
            : height
              ? undefined
              : RESIZE_DEFAULT_WIDTH;
    const dpr = window.devicePixelRatio || 1;

    const defaultResizeOptions: ImageResizeOptions = {
        dpr,
        width,
        height,
        fit: 'contain',
    };

    return { ...defaultResizeOptions, ...options.resize };
}

export type ProgressiveImageProps = ImageProps;

/**
 * Image component that shows a  shimmer animation while loading.
 */
export function ProgressiveImage({ ref, ...props }: ProgressiveImageProps) {
    const { className, onLoad, onError, ...rest } = props;
    const theme = React.useContext(ThemeContext);
    const src = useResolvedSrc(props, theme);
    const [loading, setLoading] = React.useState(() => checkImageSrcIsLoaded(src));
    const previousSrc = usePrevious(src);
    React.useEffect(() => {
        if (previousSrc && previousSrc !== src) {
            setLoading(checkImageSrcIsLoaded(src));
        }
    }, [src, previousSrc]);
    return (
        <Image
            ref={ref}
            aria-busy={loading ? 'true' : undefined}
            className={clsx(loading && 'shimmer', className)}
            onLoad={(event) => {
                onLoad?.(event);
                setLoading(false);
            }}
            onError={(event) => {
                onError?.(event);
                setLoading(false);
            }}
            {...rest}
        />
    );
}

function checkImageSrcIsLoaded(src: string | undefined): boolean {
    if (!src) {
        return false;
    }
    const img = new window.Image();
    img.src = src;

    return img.complete;
}

type ImageIconProps = ImageProps & { src: string; size?: number };

/**
 * Wrapper around Image to create an icon.
 * Basically ignore the `style` prop and provide a `size` prop.
 */
export function ImageIcon(props: ImageIconProps) {
    const { src, size, style, className, ...rest } = props;
    return (
        <Image
            src={src}
            width={size}
            height={size}
            rounded={false}
            className={clsx('align-middle', className)}
            {...rest}
        />
    );
}

export type ImagePreviewProps = React.ComponentPropsWithRef<'div'> &
    Omit<GenericButtonHookProps, 'ref'>;

export function ImagePreview({ ref: forwardedRef, ...props }: ImagePreviewProps) {
    const { disabled, onPress, onPressStart, preventFocusOnPress, ...rest } = props;
    const ref = useObjectRef(forwardedRef);
    const { buttonProps, isDisabled, isFocusVisible } = useButton({
        elementType: 'div',
        disabled,
        onPress,
        onPressStart,
        preventFocusOnPress,
        ...props,
    });
    const isPressable = Boolean(props.onPress && !isDisabled);
    return (
        <div
            ref={ref}
            {...rest}
            {...buttonProps}
            className={clsx(
                'group/image-preview',
                'relative flex min-h-0 items-center justify-center overflow-hidden rounded-sm border transition',
                isFocusVisible && 'focus-ring',
                // Can be removed when Tailwind styles are not !important
                !props.style?.color && 'text-muted',
                !(props.style?.backgroundColor || props.style?.background) &&
                    clsx('bg-muted', isPressable && 'hover:bg-standout-base'),
                isPressable && 'hover:border-standout',
                props.className
            )}
        />
    );
}

export function ImagePreviewIcon() {
    return <Icon.Image size={18} className="text-current" />;
}

export type ImagePreviewRemoveButtonProps = Pick<ButtonProps, 'onPress'>;

export function ImagePreviewRemoveButton({
    ref,
    ...props
}: ImagePreviewRemoveButtonProps & { ref?: React.ForwardedRef<HTMLButtonElement> }) {
    return (
        <Button
            ref={ref}
            leadingIcon={Icon.X}
            label="Remove"
            kind="secondary"
            iconOnly
            className={clsx(
                'absolute top-1 right-1 opacity-0',
                'group-hover/image-preview:opacity-100 focus:opacity-100'
            )}
            {...props}
        />
    );
}
